import { useAuth0 } from '@auth0/auth0-react'
import { CaptureContext, captureException, captureMessage } from '@sentry/core'
import { useRuntimeVariables } from 'app-kit'
import rawAxios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios'
import { AuthInactivityMonitor } from 'features/auth/AuthInactivityMonitor'
import {
  GeckoAuthenticationContext,
  GeckoLogoutParams,
} from 'features/auth/geckoAuthContext'
import { jwtDecode } from 'jwt-decode'
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

/**
 * A component which handles Auth0 authentication, providing a GeckoAuthContext
 * to the rest of the app
 * @param children        The rest of the app (this component is a simple wrapper)
 */
export const Auth0AuthContext: FC<{
  children: ReactNode
}> = ({ children }) => {
  const auth0 = useAuth0()
  const { AUTH0_AUDIENCE, AUTH0_CLIENT_ID } = useRuntimeVariables()
  const [authorization, setAuthorization] = useState<string>()
  const [validRefreshToken, setValidRefreshToken] = useState<boolean>(true)
  const [tokenExpiration, setTokenExpiration] = useState<Date>()

  const attemptGetAccessToken = React.useCallback(() => {
    auth0
      .getAccessTokenSilently({
        authorizationParams: { audience: AUTH0_AUDIENCE },
      })
      .then((token) => {
        const parsedJwt = jwtDecode(token)
        if (!parsedJwt.exp) {
          throw new Error('Auth0 jwt contains no expiration date')
        }
        const parsedExpirationDate = new Date(parsedJwt.exp * 1000)
        setTokenExpiration(parsedExpirationDate)
        setAuthorization(`Bearer ${token}`)
        setValidRefreshToken(true)

        captureMessage('Token retrieved from Auth0', {
          tags: { host: window.location.origin, exp: parsedJwt.exp },
        })
      })
      .catch((err) => {
        const cachedRefreshToken = localStorage.getItem(
          `@@auth0spajs@@::${AUTH0_CLIENT_ID}::${AUTH0_AUDIENCE}::openid profile email offline_access`
        )
        const captureContext: CaptureContext = { tags: {} }
        if (cachedRefreshToken) {
          const cachedTokenJson = JSON.parse(cachedRefreshToken)
          captureContext.tags!.refreshTokenExp = cachedTokenJson.expiresAt
        }

        captureException(err, captureContext)
        setValidRefreshToken(false)
      })
  }, [auth0, AUTH0_AUDIENCE, AUTH0_CLIENT_ID])

  // only get the access token here on the first render
  // all subsequent token retrievals should be handled by the inactivity monitor
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(attemptGetAccessToken, [])

  const authorizedAxios = useMemo(() => {
    const newAxios = rawAxios.create()
    newAxios.interceptors.request.use((requestConfig: AxiosRequestConfig) => {
      return {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          Authorization: authorization ?? null,
        } as AxiosRequestHeaders,
      }
    })
    return newAxios
  }, [authorization])

  const logout = useCallback(
    (geckoLogoutParams: GeckoLogoutParams) => {
      auth0.logout({ logoutParams: { returnTo: geckoLogoutParams.returnTo } })
    },
    [auth0]
  )

  return (
    <>
      <AuthInactivityMonitor
        tokenExpiration={tokenExpiration}
        validRefreshToken={validRefreshToken}
        attemptGetAccessToken={attemptGetAccessToken}
        redirectToLogin={() => {
          auth0.loginWithRedirect({
            appState: {
              returnTo:
                window.location.pathname +
                window.location.search +
                window.location.hash,
            },
          })
        }}
      />
      <GeckoAuthenticationContext.Provider
        value={{
          authorizedAxios,
          authorization,
          isAuthenticated: !!authorization,
          logout: logout,
        }}>
        {children}
      </GeckoAuthenticationContext.Provider>
    </>
  )
}
